import { useEffect, useRef, useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { MapPin } from 'lucide-react';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface YMaps3 {
  YMap: new (container: HTMLElement, props: any, children?: any[]) => any;
  YMapDefaultSchemeLayer: new (props?: any) => any;
  YMapDefaultFeaturesLayer: new (props?: any) => any;
  YMapMarker: new (props: any, element?: HTMLElement) => any;
  YMapListener: new (props: any) => any;
}

declare global {
  interface Window {
    ymaps3: YMaps3 | undefined;
  }
}

export interface MapMarker {
  id: number;
  lat: number;
  lng: number;
  name: string;
}

interface YandexMapProps {
  center?: [number, number];
  zoom?: number;
  markers?: MapMarker[];
  selectedMarkerId?: number | null;
  onMarkerClick?: (id: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  selectedPoint?: { lat: number; lng: number } | null;
  className?: string;
  height?: string;
}

const YANDEX_API_KEY = import.meta.env.VITE_YANDEX_MAPS_API_KEY as string | undefined;
const DEFAULT_CENTER: [number, number] = [55.7558, 37.6173];
const DEFAULT_ZOOM = 10;

let loadPromise: Promise<YMaps3> | null = null;

function loadYmapsApi(): Promise<YMaps3> {
  if (window.ymaps3) return Promise.resolve(window.ymaps3);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<YMaps3>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${YANDEX_API_KEY}&lang=ru_RU`;
    script.async = true;
    script.onload = () => {
      const checkReady = () => {
        if (window.ymaps3) {
          resolve(window.ymaps3);
        } else {
          setTimeout(checkReady, 50);
        }
      };
      checkReady();
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Yandex Maps API'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

const YandexMap: React.FC<YandexMapProps> = ({
  center,
  zoom = DEFAULT_ZOOM,
  markers = [],
  selectedMarkerId,
  onMarkerClick,
  onMapClick,
  selectedPoint,
  className,
  height = '320px',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerElementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const [apiError, setApiError] = useState(false);
  const [loading, setLoading] = useState(true);

  const mapCenter = center ?? DEFAULT_CENTER;

  const createMarkerElement = useCallback(
    (marker: MapMarker, isSelected: boolean): HTMLElement => {
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.flexDirection = 'column';
      el.style.alignItems = 'center';
      el.style.transform = 'translate(-50%, -100%)';

      const pin = document.createElement('div');
      pin.style.width = isSelected ? '40px' : '32px';
      pin.style.height = isSelected ? '40px' : '32px';
      pin.style.borderRadius = '50% 50% 50% 0';
      pin.style.transform = 'rotate(-45deg)';
      pin.style.background = isSelected ? '#4f46e5' : '#6366f1';
      pin.style.border = isSelected ? '3px solid #312e81' : '2px solid #fff';
      pin.style.boxShadow = isSelected
        ? '0 4px 14px rgba(79, 70, 229, 0.5)'
        : '0 2px 8px rgba(0, 0, 0, 0.2)';
      pin.style.transition = 'all 0.2s ease';
      pin.style.display = 'flex';
      pin.style.alignItems = 'center';
      pin.style.justifyContent = 'center';

      const innerDot = document.createElement('div');
      innerDot.style.width = '8px';
      innerDot.style.height = '8px';
      innerDot.style.borderRadius = '50%';
      innerDot.style.background = '#fff';
      innerDot.style.transform = 'rotate(45deg)';
      pin.appendChild(innerDot);

      el.appendChild(pin);

      if (isSelected) {
        const label = document.createElement('div');
        label.textContent = marker.name;
        label.style.marginTop = '4px';
        label.style.padding = '2px 8px';
        label.style.background = '#312e81';
        label.style.color = '#fff';
        label.style.borderRadius = '6px';
        label.style.fontSize = '11px';
        label.style.fontWeight = '600';
        label.style.whiteSpace = 'nowrap';
        label.style.maxWidth = '160px';
        label.style.overflow = 'hidden';
        label.style.textOverflow = 'ellipsis';
        label.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)';
        el.appendChild(label);
      }

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onMarkerClick?.(marker.id);
      });

      return el;
    },
    [onMarkerClick],
  );

  const createSelectedPointElement = useCallback((): HTMLElement => {
    const el = document.createElement('div');
    el.style.transform = 'translate(-50%, -50%)';
    el.style.width = '20px';
    el.style.height = '20px';
    el.style.borderRadius = '50%';
    el.style.background = '#ef4444';
    el.style.border = '3px solid #fff';
    el.style.boxShadow = '0 2px 10px rgba(239, 68, 68, 0.5)';

    const pulse = document.createElement('div');
    pulse.style.position = 'absolute';
    pulse.style.top = '-8px';
    pulse.style.left = '-8px';
    pulse.style.width = '36px';
    pulse.style.height = '36px';
    pulse.style.borderRadius = '50%';
    pulse.style.border = '2px solid rgba(239, 68, 68, 0.3)';
    pulse.style.animation = 'ymapPulse 2s infinite';
    el.appendChild(pulse);

    return el;
  }, []);

  useEffect(() => {
    if (!YANDEX_API_KEY || !containerRef.current) {
      setApiError(true);
      setLoading(false);
      return;
    }

    let destroyed = false;

    const init = async () => {
      try {
        const ymaps3 = await loadYmapsApi();
        if (destroyed || !containerRef.current) return;

        containerRef.current.innerHTML = '';

        const map = new ymaps3.YMap(containerRef.current, {
          location: {
            center: [mapCenter[1], mapCenter[0]], // CRITICAL: ymaps3 expects [lng, lat] not [lat, lng]
            zoom,
          },
        }, [
          new ymaps3.YMapDefaultSchemeLayer({}),
          new ymaps3.YMapDefaultFeaturesLayer({}),
        ]);

        if (onMapClick) {
          const listener = new ymaps3.YMapListener({
            onClick: (_: any, event: { coordinates: [number, number] }) => {
              if (event?.coordinates) {
                onMapClick(event.coordinates[1], event.coordinates[0]); // [lng, lat] → (lat, lng)
              }
            },
          });
          map.addChild(listener);
        }

        mapInstanceRef.current = map;
        setLoading(false);
      } catch {
        if (!destroyed) {
          setApiError(true);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      destroyed = true;
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.destroy();
        } catch { /* cleanup */ }
        mapInstanceRef.current = null;
      }
      markerElementsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.ymaps3) return;

    const ymaps3 = window.ymaps3;
    const prevMarkers = markerElementsRef.current;

    prevMarkers.forEach((el, key) => {
      try {
        const markerObj = (el as any).__ymapsMarker;
        if (markerObj) map.removeChild(markerObj);
      } catch { /* cleanup */ }
      prevMarkers.delete(key);
    });

    for (const marker of markers) {
      const isSelected = marker.id === selectedMarkerId;
      const el = createMarkerElement(marker, isSelected);
      const ymapMarker = new ymaps3.YMapMarker(
        { coordinates: [marker.lng, marker.lat] }, // [lng, lat]
        el,
      );
      (el as any).__ymapsMarker = ymapMarker;
      map.addChild(ymapMarker);
      prevMarkers.set(`branch-${marker.id}`, el);
    }

    if (selectedPoint) {
      const el = createSelectedPointElement();
      const ymapMarker = new ymaps3.YMapMarker(
        { coordinates: [selectedPoint.lng, selectedPoint.lat] },
        el,
      );
      (el as any).__ymapsMarker = ymapMarker;
      map.addChild(ymapMarker);
      prevMarkers.set('selected-point', el);
    }
  }, [markers, selectedMarkerId, selectedPoint, createMarkerElement, createSelectedPointElement]);

  if (apiError || !YANDEX_API_KEY) {
    return (
      <div
        className={cn(
          'relative rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-3 text-gray-400',
          className,
        )}
        style={{ height }}
      >
        <MapPin className="w-10 h-10 text-gray-300" />
        <div className="text-center px-4">
          <p className="text-sm font-medium text-gray-500">Карта недоступна</p>
          <p className="text-xs text-gray-400 mt-1">
            Для отображения карты необходим API-ключ Яндекс.Карт
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative rounded-xl overflow-hidden', className)} style={{ height }}>
      <style>{`
        @keyframes ymapPulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.8); opacity: 0; }
        }
      `}</style>
      <div ref={containerRef} className="w-full h-full" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-500">Загрузка карты...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default YandexMap;
